'use client';

import { useQuery } from '@tanstack/react-query';
import {
  createQueryKey,
  QueryDisabledError,
  usePrimaryChainVertexClient,
} from '@vertex-protocol/react-client';
import { BigDecimal } from '@vertex-protocol/utils';
import { CandleData } from 'client/modules/trading/chart/LightweightChart';

const DEFAULT_PERIOD = 3600; // 1-hour candles
const DEFAULT_LIMIT = 200;

// Periods the Nado indexer natively supports
const NATIVE_PERIODS = [60, 300, 900, 3600, 14400, 86400];

// Map of synthetic periods to their base period + aggregation factor
// e.g., 30m = 2 × 15m candles
const SYNTHETIC_PERIODS: Record<number, { base: number; factor: number }> = {
  1800: { base: 900, factor: 2 }, // 30m = 2 × 15m
};

interface EdgeCandlestick {
  time: BigDecimal;
  open: BigDecimal;
  high: BigDecimal;
  low: BigDecimal;
  close: BigDecimal;
  volume: BigDecimal;
}

/** Aggregate candles by combining `factor` consecutive candles into one */
function aggregateCandles(candles: CandleData[], factor: number): CandleData[] {
  const result: CandleData[] = [];
  for (let i = 0; i < candles.length; i += factor) {
    const group = candles.slice(i, i + factor);
    if (!group.length) break;
    result.push({
      time: group[0].time,
      open: group[0].open,
      high: Math.max(...group.map((c) => c.high)),
      low: Math.min(...group.map((c) => c.low)),
      close: group[group.length - 1].close,
      volume: group.reduce((sum, c) => sum + (c.volume ?? 0), 0),
    });
  }
  return result;
}

/**
 * Fetches OHLC candlestick data from the Nado indexer for the LightweightChart.
 * Supports synthetic timeframes (e.g., 30m) by aggregating lower-period candles.
 */
export function useLightweightChartData({
  productId,
  period = DEFAULT_PERIOD,
}: {
  productId: number | undefined;
  period?: number;
}) {
  const vertexClient = usePrimaryChainVertexClient();

  const disabled = !vertexClient || !productId;

  // Determine the actual API period and whether we need to aggregate
  const synthetic = SYNTHETIC_PERIODS[period];
  const apiPeriod = synthetic ? synthetic.base : period;
  const apiLimit = synthetic ? DEFAULT_LIMIT * synthetic.factor : DEFAULT_LIMIT;

  return useQuery({
    queryKey: createQueryKey('lightweightChartCandles', productId, period),
    queryFn: async (): Promise<CandleData[]> => {
      if (disabled) {
        throw new QueryDisabledError();
      }

      const candlesticks = await vertexClient.market.getEdgeCandlesticks({
        productId,
        period: apiPeriod as any,
        maxTimeInclusive: Math.floor(Date.now() / 1000),
        limit: apiLimit,
      });

      // Nado returns candles newest-first; reverse so oldest is first for the chart
      const candles = (candlesticks as EdgeCandlestick[])
        .slice()
        .reverse()
        .map((c) => ({
          time: c.time.times(1000).toNumber(),
          open: c.open.toNumber(),
          high: c.high.toNumber(),
          low: c.low.toNumber(),
          close: c.close.toNumber(),
          volume: c.volume.dividedBy(1e18).toNumber(),
        }));

      return synthetic ? aggregateCandles(candles, synthetic.factor) : candles;
    },
    enabled: !disabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}
